/*
 * La forma de lo que devuelve la API, escrita una vez.
 *
 * Espejo de nexlum/api/cuentas.py, escritura.py, embudo.py y vista.py. Si un campo
 * cambia en Python, cambia aquí, y TypeScript señala cada pantalla que lo usaba.
 * Los literales (Etapa, Fase, TipoHallazgo…) son los mismos que en nexlum/caso/tipos.py.
 */

export type Etapa = "sin_contactar" | "contactado" | "en_descubrimiento" | "propuesta" | "cliente" | "frio";
export type Prioridad = "Alta" | "Media" | "Baja";
export type Fase = "D0" | "D1" | "D2" | "D3" | "D4" | "D5" | "D6" | "F2";
export type TipoSesion = "S1_platica" | "S2_recorrido" | "S3_voces" | "S4_taller";
export type TipoHallazgo = "quote" | "metrica" | "dolor" | "observacion" | "oportunidad" | "ansiedad";
export type EstadoVerificacion = "candidato" | "aceptado" | "editado" | "rechazado";
export type TipoInteraccion = "reunion" | "llamada" | "correo" | "whatsapp" | "visita";

export type CasoResumen = { id: string; estado: Fase; actualizado_en: string };

export type Cuenta = {
  id: string;
  empresa: string;
  contacto: string;
  rol: string;
  giro: string;
  ubicacion: string;
  brief: string;
  prioridad: Prioridad;
  responsable_id: string;
  fit: number;
  relacional: boolean;
  notas: string;
  proxima_accion: string;
  compromiso_fecha: string | null;
  etapa: Etapa;
  fase: number; // 0–6, la más avanzada entre sus casos
  ultimo_contacto: string | null;
  dias_sin_contacto: number | null;
  casos: CasoResumen[];
  /** Requisitos del método cubiertos para pasar de fase; null si no hay caso abierto. */
  requisitos: { cumplidos: number; total: number } | null;
  n_propuestas: number;
  n_proyectos: number;
  n_pagos: number;
  creada_en: string;
};

export type Interaccion = {
  id: string;
  tipo: TipoInteraccion;
  fecha: string;
  resumen: string;
  con: string;
  quien: string;
  proxima_accion: string | null;
  compromiso_fecha: string | null;
  registrada_en: string;
  transcripcion?: string | null;
  transcripcion_archivo?: string | null;
};

export type CuentaDetalle = Cuenta & { interacciones: Interaccion[] } & CuentaComercial;

export type NuevaCuenta = {
  empresa: string;
  contacto?: string;
  responsable_id: string;
  rol?: string;
  giro?: string;
  ubicacion?: string;
  brief?: string;
  prioridad?: Prioridad;
  fit?: number;
  relacional?: boolean;
  notas?: string;
  proxima_accion?: string;
};

export type NuevaInteraccion = {
  id: string;
  tipo: TipoInteraccion;
  fecha: string;
  resumen: string;
  con?: string;
  quien: string;
  proxima_accion?: string | null;
  compromiso_fecha?: string | null;
  transcripcion?: string | null;
  transcripcion_archivo?: string | null;
};

export type InicioDescubrimiento = {
  facilitador_id: string;
  facilitador_nombre: string;
  comercial_id?: string | null;
  comercial_nombre?: string | null;
  empleados_rango?: string;
  num_sitios?: number;
  antiguedad_anios?: number;
  ruta?: "larga" | "corta";
  ruta_corta_justificacion?: string | null;
};

// ─── Hoy ────────────────────────────────────────────────────────────────────

export type EstadoItem = "proposed" | "verified" | "risk" | "discarded" | "sky" | "conflict";

export type ItemHoy = {
  cuenta_id: string;
  empresa: string;
  titulo: string;
  sub: string;
  estado: EstadoItem;
  ir: string; // ruta dentro de la app
  meta: string;
};

export type Hoy = {
  fecha: string;
  grupos: { id: "decision" | "reunion" | "enfriando" | "compromisos"; titulo: string; items: ItemHoy[] }[];
};

// ─── Expediente ─────────────────────────────────────────────────────────────

export type Persona = {
  id: string;
  nombre: string;
  rol: string;
  organizacion: "cliente" | "nexlum";
  es_economic_buyer: boolean;
  puede_vetar: boolean;
  es_champion: boolean;
  es_frontline: boolean;
  gobernanza_confirmada: boolean;
};

export type TipoEntrada = "audio" | "resumen" | "foto" | "video" | "nota" | "doc";

export type Entrada = {
  id: string;
  tipo: TipoEntrada;
  etiqueta: string;
  detalle: string;
  referencia: string | null;
  agregada_en: string;
  agregada_por: string | null;
};

export type NuevaEntrada = {
  id: string;
  tipo: TipoEntrada;
  etiqueta: string;
  detalle?: string;
  referencia?: string | null;
  agregada_por?: string | null;
};

export type Sesion = {
  id: string;
  tipo: TipoSesion;
  estado: "planificada" | "en_curso" | "cerrada";
  fecha: string;
  asistentes: string[];
  grabada: boolean;
  transcripcion?: string | null;
  transcripcion_archivo?: string | null;
  entradas: Entrada[];
};

export type Hallazgo = {
  id: string;
  tipo: TipoHallazgo;
  contenido: string;
  persona_id: string;
  sesion_id: string;
  estado_verificacion: EstadoVerificacion;
  propuesto_en: string;
  zona_caliente_id: string | null;
  atributos: Record<string, unknown> | null;
  timestamp_ms: number;
  fragmento_transcripcion: string | null;
  origen: "ia" | "manual_campo" | "manual_transcripcion";
  visibilidad: "interna" | "anonimizada" | "atribuible";
  requiere_re_verificacion: boolean | null;
};

export type Bloqueo = { id: string; nivel: "absoluto" | "duro" | "aviso"; descripcion: string; detalle: string | null };

export type Transicion = {
  desde: Fase;
  hacia: Fase;
  predicado: string;
  permitida: boolean;
  permitida_con_override: boolean;
  bloqueos: Bloqueo[];
};

export type Vista = {
  empresa: { nombre: string; giro: string; empleados_rango: string; num_sitios: number; antiguedad_anios: number };
  caso: { id: string; estado: Fase; ruta: "larga" | "corta"; facilitador: string; comercial: string | null };
  estados_orden: Fase[];
  grafo: { verificados: number; propuestos: number; por_tipo: Partial<Record<TipoHallazgo, number>>; zonas_activas: number; oportunidades: number };
  ritmo: { mediana: number | null; alerta: boolean };
  hallazgos_muestra: { tipo: TipoHallazgo; contenido: string; persona: string; sesion: string }[];
  cartera: { rank: number | null; titulo: string; score: number | null; califica: boolean; riesgo_adopcion: boolean | null }[];
  zonas: { nombre: string; estado: string; hallazgos: number; delta_magnitud: string | null }[];
  primer_encargo: {
    estado: string;
    oportunidad_id: string;
    compuertas: { clave: string; descripcion: string; cumple: boolean | null; justificacion: string | null }[];
  } | null;
  siguientes_transiciones: Transicion[];
  fecha_efectiva: { fecha: string | null; motivo: string };
  invariantes_rotos: number;
  invariantes: { invariante: string; descripcion: string; entidad: string; entidad_id: string }[];
  cierre: {
    oportunidad: { titulo: string; una_linea: string; score: number | null } | null;
    baseline: {
      numero: number; unidad: string;
      fuente: { sistema: string; reporte: string; filtros: string; captura: string | null };
      ventana: { inicio: string; fin: string };
      medicion_lista: boolean; validado_por: string | null;
    } | null;
    indicador: {
      kpi: string; ventana: { inicio: string; fin: string }; fuente_datos: string;
      metodo_verificacion: string; acordado_en: string; acordado_por: string[];
    } | null;
    terminos: { base_fija: number; fee_at_risk_pct: number; en_riesgo: number; dependencias: number; exclusiones: number } | null;
    la_piedra: { numero_escrito: string; entregada_en: string } | null;
  };
};

export type NuevaSesion = {
  id: string;
  tipo: TipoSesion;
  fecha: string;
  asistentes: string[];
  hubo_grabacion: boolean;
  consentimiento_de_todos?: boolean;
  referencia_grabacion?: string | null;
  sin_grabacion_motivo?: string | null;
  transcripcion?: string | null;
  transcripcion_archivo?: string | null;
};

export type NuevoHallazgo = {
  id: string;
  tipo: TipoHallazgo;
  contenido: string;
  persona_id: string;
  sesion_id: string;
  timestamp_ms: number;
  origen?: "manual_campo" | "manual_transcripcion";
  fragmento_transcripcion?: string | null;
  zona_caliente_id?: string | null;
  atributos?: {
    valor?: number; unidad?: string; periodo?: string; fuente_dato?: string; es_candidato_baseline?: boolean;
    intensidad?: 1 | 2 | 3 | 4 | 5; tema?: "desplazamiento" | "control" | "calidad" | "carga" | "otro"; mitigable?: boolean;
    frecuencia?: "diaria" | "semanal" | "mensual" | "esporadica"; afectados_estimados?: number;
  } | null;
};

export type NuevaPersona = {
  id: string;
  nombre: string;
  rol: string;
  organizacion: "cliente" | "nexlum";
  es_economic_buyer?: boolean;
  puede_vetar?: boolean;
  es_champion?: boolean;
  es_frontline?: boolean;
};

// ─── Vocabulario de la interfaz (el de Pablo, no el del modelo) ─────────────

export const FASES: { id: Fase; nombre: string; corto: string }[] = [
  { id: "D0", nombre: "Radar", corto: "Radar" },
  { id: "D1", nombre: "La Plática", corto: "Plática" },
  { id: "D2", nombre: "El Recorrido", corto: "Recorrido" },
  { id: "D3", nombre: "Las Voces", corto: "Voces" },
  { id: "D4", nombre: "Taller de Enfoque", corto: "Taller" },
  { id: "D5", nombre: "Cierre · baseline y términos", corto: "Cierre" },
  { id: "D6", nombre: "Propuesta", corto: "Propuesta" },
];

export const ETAPAS: Record<Etapa, { label: string; color: string; bg: string }> = {
  sin_contactar: { label: "Sin contactar", color: "var(--faint)", bg: "var(--sunken)" },
  contactado: { label: "Contactado", color: "var(--proposed)", bg: "var(--proposed-bg)" },
  en_descubrimiento: { label: "En descubrimiento", color: "var(--sky)", bg: "#E8F0FE" },
  propuesta: { label: "Propuesta enviada", color: "var(--conflict)", bg: "var(--conflict-bg)" },
  cliente: { label: "Cliente", color: "var(--verified)", bg: "var(--verified-bg)" },
  frio: { label: "Enfriándose", color: "var(--risk)", bg: "var(--risk-bg)" },
};

export const SESIONES: { id: TipoSesion; num: number; nombre: string; quien: string; dur: string; meta: string }[] = [
  { id: "S1_platica", num: 1, nombre: "La Plática", quien: "Pablo + Javier + dueño", dur: "90 min", meta: "Confianza, visión y primeras zonas calientes. Sin laptops: libreta y Plaud." },
  { id: "S2_recorrido", num: 2, nombre: "El Recorrido", quien: "Javier + guía de operaciones", dur: "2–3 h", meta: "Ver la realidad, no el Happy Path. VSM de 20 minutos por zona caliente." },
  { id: "S3_voces", num: 3, nombre: "Las Voces", quien: "Javier o Pablo, 1 a 1", dur: "30–45 min c/u", meta: "Dolor real, Happy Path vs. realidad, ansiedades de la gente." },
  { id: "S4_taller", num: 4, nombre: "El Taller de Enfoque", quien: "Pablo + Javier + dueño + jefes", dur: "2 h", meta: "Elegir EL proceso y acordar indicador + baseline. Arranca D5." },
];

export const TIPOS_HALLAZGO: Record<TipoHallazgo, string> = {
  quote: "cita", metrica: "métrica", dolor: "dolor", observacion: "observación", oportunidad: "oportunidad", ansiedad: "ansiedad",
};

export const TIPOS_ENTRADA: Record<TipoEntrada, { glifo: string; label: string }> = {
  audio: { glifo: "◉", label: "Grabación" }, resumen: { glifo: "≡", label: "Resumen Plaud" }, foto: { glifo: "▣", label: "Foto" },
  video: { glifo: "▶", label: "Video" }, nota: { glifo: "✎", label: "Nota" }, doc: { glifo: "▤", label: "Documento" },
};

export const TIPOS_INTERACCION: Record<TipoInteraccion, string> = {
  reunion: "Reunión", llamada: "Llamada", correo: "Correo", whatsapp: "WhatsApp", visita: "Visita",
};

// Las dos personas de Nexlum. Se usan como facilitador/comercial y como verificador.
export const EQUIPO: { id: string; nombre: string }[] = [
  { id: "p-pab", nombre: "Pablo" },
  { id: "p-jav", nombre: "Javier" },
];

export function nombreEquipo(id: string): string {
  return EQUIPO.find((p) => p.id === id)?.nombre ?? id;
}

// ─── Comercial: propuesta → proyecto → cobros ────────────────────────────────

export type EstadoPropuesta = "borrador" | "enviada" | "aceptada" | "rechazada";
export type EstadoProyecto = "por_arrancar" | "activo" | "cerrado";
export type EstadoTarea = "por_hacer" | "en_curso" | "bloqueado" | "hecho";
export type Direccion = "baja" | "sube";

export type PagoPlan = { concepto: string; pct: number; vence: string | null };

export type Propuesta = {
  id: string; version: number; nombre: string; encargo: string; indicador: string; unidad: string;
  baseline: number | null; meta: number | null; direccion: Direccion; fuente: string; valida: string;
  base: number; riesgo_pct: number; pagos: PagoPlan[]; estado: EstadoPropuesta; caso_id: string | null;
  creada_en: string; enviada_en: string | null; resuelta_en: string | null; proyecto_id: string | null;
};

export type NuevaPropuesta = Omit<Propuesta, "id" | "version" | "estado" | "creada_en" | "enviada_en" | "resuelta_en" | "proyecto_id"> & { enviar?: boolean };

export type Ventana = { inicio: string; fin: string };
export type Lectura = { fecha: string; valor: number };
export type IndicadorProyecto = {
  nombre: string; unidad: string; baseline: number; meta: number; direccion: Direccion; fuente: string; valida: string;
  ventana: Ventana | null; lecturas: Lectura[]; proxima_lectura: string | null; resultado: "cumplido" | "no_cumplido" | null;
};
export type Tarea = { id: string; fase: number; titulo: string; estado: EstadoTarea; quien: string; ini: string; fin: string; hito: boolean; bloqueo: string | null };
export type Dependencia = { texto: string; desde: string; quien: string; abierta: boolean };

export type Proyecto = {
  id: string; propuesta_id: string; nombre: string; encargo: string; responsable_id: string;
  indicador: IndicadorProyecto; honorarios_base: number; honorarios_riesgo: number;
  estado: EstadoProyecto; fase: number; inicio: string | null; fin_plan: string | null;
  plan: Ventana[]; tareas: Tarea[]; dependencias: Dependencia[]; cierre: string | null; creado_en: string;
  cuenta_id: string; cuenta: string;
};

export type Pago = {
  id: string; concepto: string; monto: number; vence: string; proyecto_id: string | null; proyecto: string;
  estado: "pendiente" | "cobrado"; condicionado: boolean; factura: string | null;
  facturado_en: string | null; cobrado_en: string | null; creado_en: string;
  cuenta_id: string; cuenta: string;
};

export type CuentaComercial = { propuestas: Propuesta[]; proyectos: Omit<Proyecto, "cuenta_id" | "cuenta">[]; pagos: Omit<Pago, "cuenta_id" | "cuenta">[] };

export const FASES_PROYECTO: { id: string; nombre: string; desc: string }[] = [
  { id: "P1", nombre: "Arranque", desc: "Baseline firmado, accesos y dependencias del cliente" },
  { id: "P2", nombre: "Construcción", desc: "La herramienta alrededor del proceso elegido" },
  { id: "P3", nombre: "Piloto en vivo", desc: "En operación real, con la gente del piso" },
  { id: "P4", nombre: "Medición", desc: "Ventana de 30 días del indicador" },
  { id: "P5", nombre: "Permanecer", desc: "Traspaso, manual y cierre del encargo" },
];

export const ESTADOS_TAREA: Record<EstadoTarea, { label: string; color: string; bg: string }> = {
  por_hacer: { label: "Por hacer", color: "var(--faint)", bg: "var(--sunken)" },
  en_curso: { label: "En curso", color: "var(--sky)", bg: "#E8F0FE" },
  bloqueado: { label: "Bloqueado", color: "var(--risk)", bg: "var(--risk-bg)" },
  hecho: { label: "Hecho", color: "var(--verified)", bg: "var(--verified-bg)" },
};

export const ESTADOS_PROPUESTA: Record<EstadoPropuesta, { label: string; color: string; bg: string }> = {
  borrador: { label: "Borrador", color: "var(--muted)", bg: "var(--sunken)" },
  enviada: { label: "Enviada", color: "var(--conflict)", bg: "var(--conflict-bg)" },
  aceptada: { label: "Aceptada", color: "var(--verified)", bg: "var(--verified-bg)" },
  rechazada: { label: "Rechazada", color: "var(--risk)", bg: "var(--risk-bg)" },
};

// ─── El embudo del descubrimiento: zonas → cartera → primer encargo ─────────

export type EstadoZona = "marcada" | "finalista" | "descartada";

export type Zona = {
  id: string;
  nombre: string;
  estado: EstadoZona;
  hallazgos_evidencia: string[];
  tiene_proceso_declarado: boolean;
  tiene_proceso_observado: boolean;
  delta_magnitud: string | null;
  delta_confirmado: boolean;
};

export type Voto = { evaluador_id: string; criterio: string; valor: number };

export type Oportunidad = {
  id: string;
  titulo: string;
  zona_caliente_id: string;
  hallazgos_ancla: string[];
  disposicion: string;
  rank: number | null;
  score: number | null;
  tiene_consenso: boolean;
  evaluacion_abierta: boolean;
  riesgo_adopcion: string | null;
  votos: Voto[];
  origen?: "recuperado" | "divergido" | "manual";
  arquetipo_id?: string | null;
};

// ─── Playground: conversaciones con la IA ───────────────────────────────────

export type EstadoIdea = "nueva" | "guardada" | "descartada" | "convertida";

export type IdeaExplorada = {
  id: string;
  titulo: string;
  una_linea: string;
  como_funciona: string;
  hallazgos_ancla: string[];
  arquetipo_id: string | null;
  toca_ansiedades: string;
  dato_baseline: string;
  primer_paso_30d: string;
  estado: EstadoIdea;
  motivo_descarte: string | null;
  oportunidad_id: string | null;
  avisos: string[];
  anclas_descartadas: string[];
};

/** Las piezas del contexto que se pueden apagar; hallazgos, sesiones e interacciones se eligen una por una. */
export type ContextoRonda = "empresa" | "proceso" | "ideas_previas" | "taxonomia";
export const CONTEXTOS_RONDA: { id: ContextoRonda; label: string; pista: string }[] = [
  { id: "empresa", label: "Perfil de la empresa", pista: "Giro, tamaño, sitios y lo que no se toca" },
  { id: "proceso", label: "Cómo se hace hoy", pista: "Los pasos observados en El Recorrido" },
  { id: "ideas_previas", label: "Ideas anteriores", pista: "Para no repetir lo propuesto ni lo descartado" },
  { id: "taxonomia", label: "Taxonomía de Nexlum", pista: "Los patrones conocidos; sin ella, toda idea es nueva" },
];

/** Niveles de razonamiento de Claude (output_config.effort). */
export type Esfuerzo = "low" | "medium" | "high" | "xhigh" | "max";
export const ESFUERZOS: { id: Esfuerzo; label: string; pista: string }[] = [
  { id: "low", label: "Rápido", pista: "Responde en segundos; para preguntas sencillas" },
  { id: "medium", label: "Normal", pista: "El equilibrio de siempre" },
  { id: "high", label: "Profundo", pista: "Piensa más; tarda más" },
  { id: "xhigh", label: "Muy profundo", pista: "Para comparar o desglosar a fondo" },
  { id: "max", label: "Máximo", pista: "Todo lo que puede pensar; lo más lento y caro" },
];

export type ContextoConversacion = {
  hallazgos_incluidos: string[];
  candidatos_incluidos: string[];
  sesiones_incluidas: string[];
  interacciones_incluidas: string[];
  contextos: ContextoRonda[];
  instrucciones: string | null;
};

export type MensajeExploracion = {
  id: string;
  rol: "usuario" | "asistente";
  texto: string;
  creado_en: string;
  ideas: IdeaExplorada[];
  autor: string | null;
  contexto: ContextoConversacion | null;
  esfuerzo: Esfuerzo;
  modelo: string | null;
};

export type ConversacionExploracion = {
  id: string;
  zona_caliente_id: string | null;
  titulo: string;
  creada_en: string;
  mensajes: MensajeExploracion[];
  creada_por: string | null;
};

export type ResumenConversacion = {
  id: string;
  titulo: string;
  zona_caliente_id: string | null;
  creada_en: string;
  actualizada_en: string;
  mensajes: number;
  ideas: number;
  guardadas: number;
  fichas: number;
};

export type NuevoMensaje = {
  zona_caliente_id: string | null;
  texto: string;
  contexto: ContextoConversacion;
  esfuerzo: Esfuerzo;
  conversacion_id: string | null;
};

export type ContextoExploracion = {
  instrucciones_default: string;
  mensaje_inicial_default: string;
  mensaje_inicial_general: string;
  modelo: string;
  arquetipos: { id: string; nombre: string }[];
  sesiones: { id: string; tipo: TipoSesion; fecha: string; caracteres: number; palabras: number }[];
  interacciones: { id: string; tipo: TipoInteraccion; fecha: string; resumen: string; caracteres: number; palabras: number }[];
};

export type VistaPreviaPrompt = { sistema: string; mensajes: { role: "user" | "assistant"; content: string }[]; effort: string; modelo: string; caracteres: number };

export type Compuerta = { clave: string; descripcion: string; cumple: boolean | null; justificacion: string | null };
export type PrimerEncargo = { estado: string; oportunidad_id: string; compuertas: Compuerta[] } | null;

export type PasoProceso = {
  nombre: string;
  responsable_rol?: string | null;
  es_manual?: boolean;
  es_reproceso?: boolean;
  sistema_usado?: string | null;
  duracion_seg?: number | null;
  espera_seg?: number | null;
};

/** Los cinco criterios de la Matriz, con su peso. El consenso es lo que decide. */
export const CRITERIOS: { id: "impacto" | "tiempo" | "factibilidad" | "baseline" | "respeto"; label: string; peso: number }[] = [
  { id: "impacto", label: "Impacto en el negocio", peso: 30 },
  { id: "tiempo", label: "Tiempo al resultado visible", peso: 30 },
  { id: "factibilidad", label: "Factibilidad técnica y de datos", peso: 20 },
  { id: "baseline", label: "Facilidad de medir el baseline", peso: 15 },
  { id: "respeto", label: "Respeto / bajo riesgo humano", peso: 5 },
];

export type Criterio = (typeof CRITERIOS)[number]["id"];
export type Puntajes = Record<Criterio, number>;

export const ESTADOS_ZONA: Record<EstadoZona, { label: string; color: string; bg: string }> = {
  marcada: { label: "Marcada", color: "var(--proposed)", bg: "var(--proposed-bg)" },
  finalista: { label: "Finalista", color: "var(--sky)", bg: "#E8F0FE" },
  descartada: { label: "Descartada", color: "var(--discarded)", bg: "var(--sunken)" },
};
